<script setup lang="ts">
// ================================
// Le tableau du temps 1 et son graphique des puissances souscrites (série UX
// du témoin, 15/10/2026, 2.1).
//
// Ce que le courtier doit vivre : en cinq secondes, pour chaque poste, ce
// qu'il paie aujourd'hui, ce que l'étude propose, ce qui a été réellement
// appelé et où se trouvent les dépassements. Les chiffres restent dans les
// colonnes (le tableau est la source des lecteurs d'écran) ; le graphique,
// masqué aux lecteurs d'écran, en montre les relations.
//
// Sous chaque ligne, deux barres fines partent de zéro sur une échelle
// commune : la barre claire en contour pour aujourd'hui, la barre pleine pour
// la puissance proposée (« Recommandé » ou « Vos puissances »). Un trait
// vertical, plus haut que la paire, marque la puissance maximale atteinte.
// Hachures orange (motif SVG, lisible imprimé) entre une puissance souscrite
// et la puissance maximale atteinte qui la dépasse ; rouges sur un C5 dont la
// réserve de disjonction est servie. Bande claire de marge sur le poste qui
// fixe la puissance, raccordement proche en pointillé, mention « relevée »
// sur un poste relevé par la règle de croissance. Un axe gradué en kVA sert de
// règle sous la dernière ligne. Une valeur absente n'a ni barre ni signe,
// jamais 0. Le calcul est dans `optim-turpe-v2.tableau.ts`, en fonctions pures.
// ================================
import { computed, useId } from "vue"
import { useI18n } from "vue-i18n"
import { getUnitePuissance } from "@modules/courtage/utils/optim-turpe-v2.helpers"
import {
    MENTION_DEPASSEMENT,
    colonneProposee,
    echelleTableauPlages,
    enteteColonneRecommandee,
    graduationsAxe,
    infoRelevee,
    legendeGraphique,
    nombreFr,
    positionPct,
    raccordementDuGraphique,
    recommandeeParPoste,
    segmentsTableauPlages,
    survolLigne,
} from "@modules/courtage/utils/optim-turpe-v2.tableau"
import type { SegmentsLigne } from "@modules/courtage/utils/optim-turpe-v2.tableau"
import { regleCroissanceDesPostes } from "@modules/courtage/utils/optim-turpe-v2.lexique"
import type { OptimTurpeV2Etude } from "@modules/courtage/types/optim-turpe-v2"

const { t } = useI18n({
    locale: "fr",
    messages: {
        fr: {
            // Vague wording du 14/10/2026 (Q3, Q5, Q6, Q21) : « Poste »,
            // « Puissance maximale atteinte », et la troisième colonne nommée
            // par `colonneProposee` (absente sous une économie non chiffrée).
            // 2.1 point 12 : l'unité dans l'en-tête, le nombre seul en cellule.
            colonnes: {
                plage: "Poste",
                aujourdhui: "Aujourd'hui ({unite})",
                pointe: "Puissance maximale atteinte ({unite})",
                propose: "{colonne} ({unite})",
            },
            // Lu par un lecteur d'écran seulement : la cellule visible reste vide.
            pointeAbsente: "non mesurée",
            valeurAbsente: "non indiquée",
            relevee: "relevée",
            raccordement: "raccordement {valeur}",
            raccordementHorsEchelle: "Raccordement : {valeur}, hors échelle",
            margeSurvol: "marge de sécurité {pct} %",
        },
    },
})

type Props = {
    etude: OptimTurpeV2Etude
    /**
     * Maquette finale du 15/10/2026 : sur la page de résultat, la règle de
     * croissance et le raccordement hors échelle se lisent dans « Pourquoi
     * ces puissances » ; le graphique les tait.
     */
    repliDesNotes?: boolean
    /**
     * Revue 2 en production (I24, I61) : le scénario affiché n'est pas le
     * recommandé ; sa colonne s'appelle « Option chiffrée ».
     */
    optionChiffree?: boolean
    /**
     * Lot 4c (D14, choix 12) : sous le graphique « Puissances par poste », le
     * tableau replié ne porte que les valeurs, pour les copier ; ni légende,
     * ni barres, ni axe.
     */
    sansGraphique?: boolean
}
const props = withDefaults(defineProps<Props>(), {
    repliDesNotes: false,
    optionChiffree: false,
    sansGraphique: false,
})
const getOptions = computed(() => ({ optionChiffree: props.optionChiffree }))

/** Identifiant des motifs de hachures, unique par instance du composant. */
const idMotif = useId()

const getUnite = computed(() => getUnitePuissance(props.etude))
const getLignes = computed(() =>
    segmentsTableauPlages(props.etude, getOptions.value)
)
/**
 * « Recommandé », « Vos puissances », « Option chiffrée », ou `null` : pas de
 * troisième colonne.
 */
const getColonne = computed(() =>
    colonneProposee(props.etude, getOptions.value)
)
/**
 * Revue du 19/10/2026 (N2) : la colonne de la puissance recommandée est
 * toujours là, égale à « Aujourd'hui » quand rien ne change ; « Vos
 * puissances » sur une étude saisie. `null` seulement quand rien n'est
 * recommandé (économie non chiffrée).
 */
const getRecommandee = computed(() =>
    recommandeeParPoste(props.etude, getOptions.value)
)
/** La valeur recommandée d'une ligne, `null` quand elle manque. */
function recommandee(ligne: SegmentsLigne): number | null {
    return getRecommandee.value?.valeurs[ligne.cle] ?? null
}
/** I39 : ce que veut dire « relevée », en info-bulle. */
const getInfoRelevee = computed(() =>
    infoRelevee(props.etude.compteur?.segment)
)
const getRaccordement = computed(() =>
    raccordementDuGraphique(props.etude, getLignes.value)
)
/** Une seule échelle pour toutes les barres : les lignes se comparent. */
const getEchelle = computed(() =>
    echelleTableauPlages(getLignes.value, getRaccordement.value)
)
const getGraduations = computed(() => graduationsAxe(getEchelle.value))
const getLegende = computed(() =>
    legendeGraphique(getLignes.value, getColonne.value, getRaccordement.value)
)
const getUneRelevee = computed(() => getLignes.value.some((l) => l.relevee))
const getNbColonnes = computed(() => (getRecommandee.value ? 4 : 3))

function pct(v: number | null): number | null {
    const echelle = getEchelle.value
    if (v === null || echelle === null || v <= 0) return null
    return positionPct(v, echelle)
}

function css(p: number): string {
    return `${p.toFixed(2)}%`
}

/** Un intervalle [de, a] de l'échelle, en `left` et `width`. */
function intervalle(de: number, a: number): { left: string; width: string } {
    const g = pct(de) ?? 0
    const d = pct(a) ?? 0
    return { left: css(g), width: css(Math.max(0, d - g)) }
}

function valeur(v: number | null): string {
    return v === null ? "" : nombreFr(v)
}

function avecUnite(v: number): string {
    return `${nombreFr(v)}\u00a0${getUnite.value}`
}

function survol(ligne: SegmentsLigne): string {
    return survolLigne(ligne, getColonne.value, getUnite.value)
}

/** La graduation garde 0 et la dernière à 375 px (point 13). */
function graduationMobile(i: number): boolean {
    return i === 0 || i === getGraduations.value.length - 1
}

const CLASSES_LEGENDE: Record<string, string> = {
    aujourdhui: "h-2 w-3 rounded-[2px] border border-surface-400 bg-white",
    propose: "h-2 w-3 rounded-[2px] bg-primary-700",
    pointe: "h-3 w-[2px] rounded-full bg-primary-900",
    marge: "h-2.5 w-3 rounded-[2px] bg-primary-300",
    raccordement: "h-3 w-0 border-l border-dashed border-primary-500",
}
</script>

<template>
    <div
        v-if="getLignes.length"
        class="optim-turpe-v2-tableau-plages flex w-full flex-col gap-1.5 [-webkit-print-color-adjust:exact] [print-color-adjust:exact]"
        data-testid="optim-turpe-v2-tableau-plages"
    >
        <!-- Les motifs de hachures (point 6), partagés par les lignes : un
             motif, pas une couleur seule, pour rester lisible imprimé. -->
        <svg aria-hidden="true" class="absolute h-0 w-0" focusable="false">
            <defs>
                <pattern
                    :id="`${idMotif}-orange`"
                    width="6"
                    height="6"
                    patternUnits="userSpaceOnUse"
                    patternTransform="rotate(45)"
                >
                    <rect
                        width="6"
                        height="6"
                        class="fill-[var(--p-orange-50)]"
                    />
                    <line
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="6"
                        class="stroke-[var(--p-orange-500)]"
                        stroke-width="3"
                    />
                </pattern>
                <pattern
                    :id="`${idMotif}-rouge`"
                    width="6"
                    height="6"
                    patternUnits="userSpaceOnUse"
                    patternTransform="rotate(45)"
                >
                    <rect width="6" height="6" class="fill-[var(--p-red-50)]" />
                    <line
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="6"
                        class="stroke-[var(--p-red-600)]"
                        stroke-width="3"
                    />
                </pattern>
            </defs>
        </svg>

        <!-- La légende en une ligne, au-dessus des barres (point 12). -->
        <p
            v-if="getLegende.length && !sansGraphique"
            aria-hidden="true"
            class="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-primary-600"
            data-testid="optim-turpe-v2-tableau-legende"
        >
            <template v-for="(el, i) in getLegende" :key="el.signe">
                <span v-if="i > 0" class="text-primary-400">·</span>
                <span class="inline-flex items-center gap-1">
                    <svg
                        v-if="
                            el.signe === 'hachures' ||
                            el.signe === 'hachures_rouges'
                        "
                        class="h-2.5 w-3"
                    >
                        <rect
                            width="100%"
                            height="100%"
                            :fill="`url(#${idMotif}-${el.signe === 'hachures' ? 'orange' : 'rouge'})`"
                        />
                    </svg>
                    <span
                        v-else
                        class="inline-block shrink-0"
                        :class="CLASSES_LEGENDE[el.signe]"
                    />
                    {{ el.texte }}
                </span>
            </template>
        </p>

        <table class="w-full border-collapse text-sm">
            <thead>
                <tr
                    class="text-left text-xs text-primary-500 max-sm:grid"
                    :class="
                        getRecommandee
                            ? 'max-sm:grid-cols-3'
                            : 'max-sm:grid-cols-2'
                    "
                >
                    <th
                        scope="col"
                        class="py-1 pr-2 font-medium max-sm:sr-only"
                    >
                        {{ t("colonnes.plage") }}
                    </th>
                    <th
                        scope="col"
                        class="px-2 py-1 text-right font-medium max-sm:pl-0"
                        data-testid="optim-turpe-v2-tableau-entete-aujourdhui"
                    >
                        {{ t("colonnes.aujourdhui", { unite: getUnite }) }}
                    </th>
                    <th
                        scope="col"
                        class="px-2 py-1 text-right font-medium"
                        data-testid="optim-turpe-v2-tableau-entete-pointe"
                    >
                        {{ t("colonnes.pointe", { unite: getUnite }) }}
                    </th>
                    <th
                        v-if="getRecommandee"
                        scope="col"
                        class="py-1 pl-2 text-right font-medium"
                        data-testid="optim-turpe-v2-tableau-entete-propose"
                    >
                        {{
                            t("colonnes.propose", {
                                colonne: enteteColonneRecommandee(
                                    getRecommandee.colonne
                                ),
                                unite: getUnite,
                            })
                        }}
                    </th>
                </tr>
            </thead>
            <tbody>
                <template v-for="ligne in getLignes" :key="ligne.cle">
                    <tr
                        class="break-inside-avoid border-t border-surface-200 align-baseline max-sm:grid"
                        :class="
                            getRecommandee
                                ? 'max-sm:grid-cols-3'
                                : 'max-sm:grid-cols-2'
                        "
                        data-testid="optim-turpe-v2-tableau-ligne"
                        :data-plage="ligne.cle"
                    >
                        <th
                            scope="row"
                            class="pt-1.5 pr-2 text-left font-normal text-primary-700"
                            :class="
                                getRecommandee
                                    ? 'max-sm:col-span-3'
                                    : 'max-sm:col-span-2'
                            "
                        >
                            {{ ligne.libelle }}
                        </th>
                        <td
                            class="whitespace-nowrap px-2 pt-1.5 text-right tabular-nums text-primary-700 max-sm:pl-0"
                            data-testid="optim-turpe-v2-tableau-aujourdhui"
                        >
                            {{ valeur(ligne.aujourdhui) }}
                            <span
                                v-if="ligne.aujourdhui === null"
                                class="sr-only"
                            >
                                {{ t("valeurAbsente") }}
                            </span>
                        </td>
                        <td
                            class="whitespace-nowrap px-2 pt-1.5 text-right tabular-nums text-primary-700"
                            data-testid="optim-turpe-v2-tableau-pointe"
                        >
                            {{ valeur(ligne.pointe) }}
                            <span v-if="ligne.pointe === null" class="sr-only">
                                {{ t("pointeAbsente") }}
                            </span>
                        </td>
                        <td
                            v-if="getRecommandee"
                            class="whitespace-nowrap pl-2 pt-1.5 text-right tabular-nums"
                            :class="
                                recommandee(ligne) !== null &&
                                recommandee(ligne) !== ligne.aujourdhui
                                    ? 'font-semibold text-primary-900'
                                    : 'text-primary-700'
                            "
                            data-testid="optim-turpe-v2-tableau-propose"
                        >
                            {{ valeur(recommandee(ligne)) }}
                            <span
                                v-if="recommandee(ligne) === null"
                                class="sr-only"
                            >
                                {{ t("valeurAbsente") }}
                            </span>
                        </td>
                    </tr>
                    <!-- Le graphique du poste : masqué aux lecteurs d'écran, le
                         tableau ci-dessus porte les mêmes chiffres. -->
                    <tr
                        v-if="!sansGraphique"
                        aria-hidden="true"
                        class="break-inside-avoid max-sm:block"
                        :data-plage-graphique="ligne.cle"
                    >
                        <td
                            :colspan="getNbColonnes"
                            class="pb-1.5 max-sm:block"
                        >
                            <div
                                class="relative mx-2 h-8 max-sm:mx-0"
                                :title="survol(ligne)"
                                data-testid="optim-turpe-v2-tableau-barre"
                                :data-plage="ligne.cle"
                            >
                                <!-- Repères verticaux des graduations. -->
                                <span
                                    v-for="g in getGraduations.slice(1)"
                                    :key="g"
                                    class="absolute inset-y-0 w-px bg-surface-200"
                                    :style="{ left: css(pct(g) ?? 0) }"
                                />
                                <!-- Bande de marge (point 5), derrière les barres. -->
                                <span
                                    v-if="ligne.marge"
                                    class="absolute inset-y-1 bg-primary-300"
                                    :style="
                                        intervalle(
                                            ligne.marge.de,
                                            ligne.marge.a
                                        )
                                    "
                                    :title="
                                        t('margeSurvol', {
                                            pct: nombreFr(ligne.marge.pct),
                                        })
                                    "
                                    data-testid="optim-turpe-v2-tableau-marge"
                                />
                                <!-- Aujourd'hui : barre claire en contour. -->
                                <span
                                    v-if="pct(ligne.aujourdhui) !== null"
                                    class="absolute left-0 top-[7px] h-2 rounded-[2px] border border-surface-400 bg-white"
                                    :style="{
                                        width: css(pct(ligne.aujourdhui) ?? 0),
                                    }"
                                    data-testid="optim-turpe-v2-tableau-barre-aujourdhui"
                                />
                                <!-- Proposé : barre pleine, juste dessous. -->
                                <span
                                    v-if="
                                        getColonne &&
                                        pct(ligne.propose) !== null
                                    "
                                    class="absolute left-0 top-[17px] h-2 rounded-[2px] bg-primary-700"
                                    :style="{
                                        width: css(pct(ligne.propose) ?? 0),
                                    }"
                                    data-testid="optim-turpe-v2-tableau-barre-propose"
                                />
                                <!-- Dépassements (point 6) ou disjonction (point 10). -->
                                <svg
                                    v-if="ligne.depassement"
                                    class="absolute h-2"
                                    :class="
                                        ligne.depassement.barre === 'propose'
                                            ? 'top-[17px]'
                                            : 'top-[7px]'
                                    "
                                    :style="
                                        intervalle(
                                            ligne.depassement.de,
                                            ligne.depassement.a
                                        )
                                    "
                                    preserveAspectRatio="none"
                                    data-testid="optim-turpe-v2-tableau-depassement"
                                    :data-nature="ligne.depassement.nature"
                                >
                                    <rect
                                        width="100%"
                                        height="100%"
                                        :fill="`url(#${idMotif}-${ligne.depassement.nature === 'disjonction' ? 'rouge' : 'orange'})`"
                                    />
                                </svg>
                                <!-- Raccordement proche (point 7). -->
                                <span
                                    v-if="getRaccordement?.proche"
                                    class="absolute -inset-y-1.5 w-0 border-l border-dashed border-primary-500"
                                    :style="{
                                        left: css(
                                            pct(getRaccordement.valeur) ?? 0
                                        ),
                                    }"
                                    data-testid="optim-turpe-v2-tableau-raccordement"
                                />
                                <!-- Puissance maximale atteinte : un trait plus
                                     haut que la paire de barres (point 4). -->
                                <span
                                    v-if="pct(ligne.pointe) !== null"
                                    class="absolute inset-y-0 w-[2px] -translate-x-1/2 rounded-full bg-primary-900"
                                    :style="{
                                        left: css(pct(ligne.pointe) ?? 0),
                                    }"
                                    data-testid="optim-turpe-v2-tableau-trait-pointe"
                                />
                            </div>
                            <!-- La mention en bout de ligne (points 6 et 10),
                                 ou « relevée » sous la barre proposée (point 8). -->
                            <p
                                v-if="ligne.depassement"
                                class="mx-2 text-right text-xs leading-4 max-sm:mx-0"
                                :class="
                                    ligne.depassement.nature === 'disjonction'
                                        ? 'text-[var(--p-red-700)]'
                                        : 'text-[var(--p-orange-700)]'
                                "
                                data-testid="optim-turpe-v2-tableau-mention"
                            >
                                {{
                                    MENTION_DEPASSEMENT[
                                        ligne.depassement.nature
                                    ]
                                }}
                            </p>
                            <p
                                v-else-if="ligne.relevee"
                                class="relative mx-2 h-4 text-xs leading-4 text-primary-700 max-sm:mx-0"
                            >
                                <span
                                    class="absolute top-0 -translate-x-full whitespace-nowrap"
                                    :style="{
                                        left: css(pct(ligne.propose) ?? 0),
                                    }"
                                    data-testid="optim-turpe-v2-tableau-relevee"
                                    >{{ t("relevee") }}
                                    <BaseTooltipMessage
                                        v-if="getInfoRelevee"
                                        :content="getInfoRelevee"
                                        size="extra-small"
                                        position="top"
                                        data-testid="optim-turpe-v2-tableau-relevee-info"
                                /></span>
                            </p>
                        </td>
                    </tr>
                </template>
                <!-- L'axe gradué, règle commune sous la dernière ligne (point 1). -->
                <tr
                    v-if="getGraduations.length && !sansGraphique"
                    aria-hidden="true"
                    class="max-sm:block"
                >
                    <td :colspan="getNbColonnes" class="max-sm:block">
                        <div
                            class="relative mx-2 h-4 border-t border-surface-300 text-xs leading-4 text-primary-500 tabular-nums max-sm:mx-0"
                            data-testid="optim-turpe-v2-tableau-axe"
                        >
                            <span
                                v-for="(g, i) in getGraduations"
                                :key="g"
                                class="absolute top-0 whitespace-nowrap"
                                :class="[
                                    i === 0 ? '' : '-translate-x-1/2',
                                    graduationMobile(i) ? '' : 'max-sm:hidden',
                                ]"
                                :style="{ left: css(pct(g) ?? 0) }"
                                data-testid="optim-turpe-v2-tableau-graduation"
                            >
                                {{ nombreFr(g)
                                }}<template
                                    v-if="i === getGraduations.length - 1"
                                    >&nbsp;{{ getUnite }}</template
                                >
                            </span>
                        </div>
                        <div
                            v-if="getRaccordement?.proche"
                            class="relative mx-2 h-4 text-xs leading-4 text-primary-600 max-sm:mx-0"
                        >
                            <span
                                class="absolute top-0 -translate-x-full whitespace-nowrap pr-1"
                                :style="{
                                    left: css(pct(getRaccordement.valeur) ?? 0),
                                }"
                            >
                                {{
                                    t("raccordement", {
                                        valeur: avecUnite(
                                            getRaccordement.valeur
                                        ),
                                    })
                                }}
                            </span>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>

        <p
            v-if="
                getRaccordement &&
                !getRaccordement.proche &&
                !repliDesNotes &&
                !sansGraphique
            "
            class="text-xs text-primary-600"
            data-testid="optim-turpe-v2-tableau-raccordement-hors-echelle"
        >
            {{
                t("raccordementHorsEchelle", {
                    valeur: `${nombreFr(getRaccordement.valeur)}\u00a0${getRaccordement.unite}`,
                })
            }}
        </p>
        <!-- Point 8 : la règle de croissance, visible sans rien déplier dès
             qu'un poste est relevé ; écrite selon le segment (un C4 n'a pas
             de pointe, relecture de L-G reprise dans L-B). -->
        <p
            v-if="getUneRelevee && !repliDesNotes && !sansGraphique"
            class="text-xs text-primary-600"
            data-testid="optim-turpe-v2-tableau-note-relevee"
        >
            {{ regleCroissanceDesPostes(etude.compteur?.segment) }}
        </p>
    </div>
</template>
